import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import {
  SHARED_TYPOGRAPHY,
  getProgressStyles,
  getProgressStylesByIndex,
  formatCategoryName,
} from '../../../theme';

const CategoryAnalyticsAccordion = ({ categoryAnalyticsData }) => {
  // Memoize total template count to avoid recalculating for each category
  const totalTemplateCount = useMemo(() => {
    if (!categoryAnalyticsData?.category_analytics) return 0;
    return categoryAnalyticsData.category_analytics.reduce(
      (sum, c) => sum + c.template_count,
      0
    );
  }, [categoryAnalyticsData?.category_analytics]);

  const hasData =
    categoryAnalyticsData?.category_analytics &&
    categoryAnalyticsData.category_analytics.length > 0;

  return (
    <Box sx={{ mt: 4 }}>
      <Accordion defaultExpanded={false}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="category-analytics-content"
          id="category-analytics-header"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CategoryIcon sx={{ fontSize: 32, color: 'info.main' }} />
            <Typography variant="h4" sx={SHARED_TYPOGRAPHY.h4Bold}>
              Category Analytics
            </Typography>
            <Typography
              variant="h6"
              sx={{ ...SHARED_TYPOGRAPHY.bodyMediumSecondary }}
            >
              ({categoryAnalyticsData?.period || 'N/A'})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Category Distribution Chart */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent
                  sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                  <Typography
                    variant="h5"
                    sx={{ mb: 2, ...SHARED_TYPOGRAPHY.h5Bold }}
                  >
                    Template Distribution by Category
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      {hasData ? (
                        categoryAnalyticsData.category_analytics.map(
                          (category, index) => (
                            <Box key={category.category} sx={{ mb: 2 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  sx={SHARED_TYPOGRAPHY.bodyBold}
                                >
                                  {formatCategoryName(category.category)}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={SHARED_TYPOGRAPHY.bodyMediumSecondary}
                                >
                                  {category.template_count} templates
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={
                                  (category.template_count /
                                    totalTemplateCount) *
                                  100
                                }
                                sx={getProgressStylesByIndex(index)}
                              />
                            </Box>
                          )
                        )
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: 'center', py: 4 }}
                        >
                          No category analytics data available
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Category Performance Comparison */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent
                  sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                  <Typography
                    variant="h5"
                    sx={{ mb: 2, ...SHARED_TYPOGRAPHY.h5Bold }}
                  >
                    Category Performance Comparison
                  </Typography>
                  <TableContainer sx={{ flex: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Category</TableCell>
                          <TableCell align="right">Templates</TableCell>
                          <TableCell align="right">Avg Success Rate</TableCell>
                          <TableCell align="right">Total Cost</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {hasData ? (
                          categoryAnalyticsData.category_analytics
                            .sort(
                              (a, b) =>
                                (b.total_cost || 0) - (a.total_cost || 0)
                            )
                            .map(category => (
                              <TableRow key={category.category}>
                                <TableCell>
                                  <Typography
                                    variant="body1"
                                    sx={SHARED_TYPOGRAPHY.bodyBold}
                                  >
                                    {formatCategoryName(category.category)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body1"
                                    sx={SHARED_TYPOGRAPHY.bodyMedium}
                                  >
                                    {category.template_count}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'flex-end',
                                      gap: 1,
                                    }}
                                  >
                                    <LinearProgress
                                      variant="determinate"
                                      value={category.avg_success_rate}
                                      sx={getProgressStyles(
                                        category.avg_success_rate
                                      )}
                                    />
                                    <Typography
                                      variant="body1"
                                      sx={SHARED_TYPOGRAPHY.bodyBold}
                                    >
                                      {category.avg_success_rate.toFixed(1)}%
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography
                                    variant="body1"
                                    sx={SHARED_TYPOGRAPHY.bodyBold}
                                  >
                                    ${(category.total_cost || 0).toFixed(4)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={4}
                              align="center"
                              sx={{ py: 4 }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No category analytics data available
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default CategoryAnalyticsAccordion;
